import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../ui/Toast';

const Signup: React.FC = () => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    email: '', password: '', confirmPassword: '', name: '', phone: '',
    agreeToTerms: false, agreeToPrivacy: false, agreeToMarketing: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const { signUp, signInWithMagicLink } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref');

  React.useEffect(() => {
    if (referralCode) { localStorage.setItem('pending_referral_code', referralCode); }
  }, [referralCode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) { toast.warning('Passwords do not match.'); return; }
    if (!formData.agreeToTerms || !formData.agreeToPrivacy) { toast.warning('Please agree to the required terms.'); return; }
    if (formData.password.length < 8) { toast.warning('Password must be at least 8 characters.'); return; }
    setIsLoading(true);
    try {
      const { user, error } = await signUp(formData.email, formData.password, { name: formData.name, phone: formData.phone });
      if (error) {
        const msg = error.message || '';
        const status = (error as { status?: number }).status ?? 0;
        if (msg.includes('User already registered')) toast.error('This email is already registered. Try signing in or resetting your password.');
        else if (msg.includes('Password should be at least')) toast.warning('Password must be at least 8 characters.');
        else if (msg.includes('Invalid email') || msg.includes('is invalid')) toast.warning('Please use a valid email address.');
        else if (msg.includes('rate limit') || msg.includes('email rate limit') || status === 429) {
          toast.error('Too many sign-up attempts. Please wait a few minutes and try again.');
        }
        // PR 7: friendly 5xx — generic supabase server errors confused users
        // into retrying with a different email instead of waiting it out.
        else if (msg.includes('Database error') || msg.includes('querying schema') || status >= 500) {
          toast.error('Sign-up service is temporarily unavailable. Please try again in a minute.');
        }
        else toast.error(`An error occurred during registration: ${msg}`);
        return;
      }
      if (user) {
        try {
          const { supabase } = await import('../../lib/supabase');
          const { data: welcomeCoupon } = await supabase.from('coupons').select('id').eq('code', 'WELCOME10').maybeSingle();
          if (welcomeCoupon) {
            await supabase
              .from('user_coupons')
              .upsert(
                { user_id: user.id, coupon_id: welcomeCoupon.id },
                { onConflict: 'user_id,coupon_id', ignoreDuplicates: true }
              );
          }
        } catch (couponError) { console.log('Welcome coupon issue failed (ignored):', couponError); }
        navigate('/signup-complete', { state: { email: formData.email, hasReferralCoupon: !!referralCode } });
      }
    } catch (error) { toast.error('An error occurred during registration. Please try again.'); } finally { setIsLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel — Brand */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[540px] flex-shrink-0 bg-gradient-to-br from-forest-600 via-forest-800 to-forest-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full border border-white/10" />
          <div className="absolute top-1/4 right-0 w-64 h-64 rounded-full border border-white/10" />
          <div className="absolute bottom-20 left-16 w-48 h-48 rounded-full bg-white/5" />
          <div className="absolute -bottom-10 -right-10 w-72 h-72 rounded-full border border-white/5" />
        </div>
        <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full">
          <Link to="/" className="inline-flex items-baseline">
            <span className="font-display text-2xl font-bold tracking-[-0.04em] text-white">Libera</span><span className="font-display text-2xl font-bold tracking-[-0.04em] text-gold-400">Cure</span>
          </Link>
          <div>
            <h2 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-4">
              Start Your<br />Health Journey
            </h2>
            <p className="text-forest-100 max-w-sm leading-relaxed">
              Create an account to access exclusive discounts, order tracking, and personalized health solutions.
            </p>
          </div>
          <div className="space-y-3 text-sm">
            {['Welcome coupon on signup', 'Track orders in real-time', 'Earn reward points on every purchase'].map((text) => (
              <div key={text} className="flex items-center gap-2.5">
                <svg className="w-4 h-4 text-forest-200 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-forest-100">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel — Form */}
      <div className="flex-1 flex items-center justify-center px-6 sm:px-12 py-10 bg-cream-100">
        <div className="w-full max-w-[420px]">
          <div className="lg:hidden mb-8">
            <Link to="/" className="inline-flex items-baseline">
              <span className="font-display text-2xl font-bold tracking-[-0.04em] text-forest-800">Libera</span><span className="font-display text-2xl font-bold tracking-[-0.04em] text-gold-400">Cure</span>
            </Link>
          </div>

          <h1 className="text-3xl font-display font-bold text-forest-900 mb-1 tracking-tight">Create account</h1>
          <p className="text-warm-500 mb-6">Join LiberaCure for exclusive benefits</p>

          {/* Magic Link sent confirmation */}
          {magicLinkSent && (
            <div className="mb-6 p-4 bg-forest-50 border border-forest-200 rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <svg className="w-5 h-5 text-forest-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-semibold text-forest-800">Sign-up link sent!</span>
              </div>
              <p className="text-xs text-forest-600">Check your inbox at <strong>{formData.email}</strong> and click the link to complete registration.</p>
            </div>
          )}

          {/* Standard signup form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-forest-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange}
                  className="w-full px-3.5 py-2.5 bg-cream-50 border border-warm-300 rounded-xl text-sm focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 outline-none transition-all" placeholder="John Doe" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-forest-700 mb-1">Phone <span className="text-red-500">*</span></label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange}
                  className="w-full px-3.5 py-2.5 bg-cream-50 border border-warm-300 rounded-xl text-sm focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 outline-none transition-all" placeholder="+1-000-000-0000" required />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-forest-700 mb-1">Email <span className="text-red-500">*</span></label>
              <input type="email" name="email" value={formData.email} onChange={handleInputChange}
                className="w-full px-3.5 py-2.5 bg-cream-50 border border-warm-300 rounded-xl text-sm focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 outline-none transition-all" placeholder="you@example.com" required />
              <p className="text-xs text-warm-400 mt-1">Used for login and order updates</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-forest-700 mb-1">Password <span className="text-red-500">*</span></label>
                <input type="password" name="password" value={formData.password} onChange={handleInputChange}
                  className="w-full px-3.5 py-2.5 bg-cream-50 border border-warm-300 rounded-xl text-sm focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 outline-none transition-all" placeholder="8+ characters" required minLength={8} />
              </div>
              <div>
                <label className="block text-sm font-medium text-forest-700 mb-1">Confirm <span className="text-red-500">*</span></label>
                <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange}
                  className="w-full px-3.5 py-2.5 bg-cream-50 border border-warm-300 rounded-xl text-sm focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 outline-none transition-all" placeholder="Re-enter" required />
              </div>
            </div>

            {/* Terms */}
            <div className="space-y-2 pt-3 border-t border-warm-200">
              {[
                { name: 'agreeToTerms', checked: formData.agreeToTerms, label: <><span className="text-red-500 font-medium">[Required]</span> I agree to the <Link to="/terms" className="text-gold-400 hover:underline">Terms of Service</Link></> },
                { name: 'agreeToPrivacy', checked: formData.agreeToPrivacy, label: <><span className="text-red-500 font-medium">[Required]</span> I agree to the <Link to="/privacy" className="text-gold-400 hover:underline">Privacy Policy</Link></> },
                { name: 'agreeToMarketing', checked: formData.agreeToMarketing, label: <>[Optional] Receive marketing communications</> },
              ].map((item) => (
                <label key={item.name} className="flex items-start gap-2.5 cursor-pointer">
                  <input type="checkbox" name={item.name} checked={item.checked} onChange={handleInputChange}
                    className="mt-0.5 w-4 h-4 text-forest-700 border-warm-300 rounded focus:ring-forest-500/20" />
                  <span className="text-xs text-warm-600 leading-relaxed">{item.label}</span>
                </label>
              ))}
            </div>

            <button type="submit" disabled={isLoading}
              className="w-full bg-forest-800 hover:bg-forest-700 disabled:bg-forest-300 text-cream-100 py-3 rounded-lg border border-forest-700/50 font-semibold text-sm transition-all cursor-pointer flex items-center justify-center shadow-sm">
              {isLoading ? <div className="w-5 h-5 border-2 border-cream-100 border-t-transparent rounded-full animate-spin" /> : 'Create Account'}
            </button>
          </form>

          {/* Magic Link — secondary */}
          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-warm-200" /></div>
            <div className="relative flex justify-center text-xs"><span className="px-4 bg-cream-100 text-warm-400 uppercase tracking-wider">or</span></div>
          </div>
          <button
            type="button"
            onClick={async () => {
              if (!formData.email.trim()) { toast.warning('Please enter your email address above first.'); return; }
              setIsLoading(true);
              try {
                const { error } = await signInWithMagicLink(formData.email);
                if (error) {
                  // PR 7: distinguish rate-limit from real failures
                  const msg = error.message || '';
                  const status = (error as { status?: number }).status ?? 0;
                  if (msg.includes('rate limit') || status === 429) {
                    toast.error('Too many requests. Please wait a few minutes and try again.');
                  } else if (status >= 500) {
                    toast.error('Email service temporarily unavailable. Please try again shortly.');
                  } else {
                    toast.error('Failed to send sign-up link: ' + msg);
                  }
                }
                else setMagicLinkSent(true);
              } catch { toast.error('An error occurred.'); }
              finally { setIsLoading(false); }
            }}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-cream-50 border border-warm-300 rounded-lg text-sm font-medium text-forest-700 hover:bg-warm-100 hover:border-warm-400 disabled:opacity-50 transition-all cursor-pointer"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Sign up with Email Link
          </button>
          <p className="text-xs text-warm-400 text-center mt-2">No password needed — just enter your email above</p>

          <p className="mt-5 text-center text-sm text-warm-500">
            Already have an account?{' '}
            <Link to="/login" className="text-gold-400 hover:text-forest-800 font-semibold">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
